import * as THREE from "three";
import type { Vec3 } from "../core";
import { sampleSecondaryMotion, type PresentationImpulse, type SecondaryResponse } from "../physics";
import type { Palette } from "./styles";

interface PivotResponder {
  pivot: THREE.Group;
  position: Vec3;
  phase: number;
}

const GRASS_RESPONSE: SecondaryResponse = {
  stiffness: 15,
  damping: 4.8,
  windAffinity: 1.3,
  impulseAffinity: 1.2,
  limit: 0.28,
};

const MOTE_RESPONSE: SecondaryResponse = {
  stiffness: 7,
  damping: 3.2,
  windAffinity: 0.82,
  impulseAffinity: 1.8,
  limit: 0.5,
};

const WATER_RESPONSE: SecondaryResponse = {
  stiffness: 18,
  damping: 4.6,
  windAffinity: 1.3,
  impulseAffinity: 2.8,
  limit: 0.18,
};

/** Coarse scenery consumers for the same field used by character gear. */
export class SecondaryEnvironment {
  readonly root = new THREE.Group();

  private readonly grassMaterial = new THREE.MeshBasicMaterial({ color: 0x516b4b, side: THREE.DoubleSide });
  private readonly moteMaterial = new THREE.MeshBasicMaterial({ color: 0xffd166, transparent: true, opacity: 0.42 });
  private readonly waterMaterial = new THREE.MeshBasicMaterial({ color: 0x315c78, transparent: true, opacity: 0.68, side: THREE.DoubleSide });
  private readonly waterEdgeMaterial = new THREE.LineBasicMaterial({ color: 0x8ee3ff, transparent: true, opacity: 0.38 });
  private readonly grass: PivotResponder[] = [];
  private readonly motes: PivotResponder[] = [];
  private readonly water: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly waterRest: Array<{ localX: number; localY: number; world: Vec3 }> = [];

  constructor() {
    this.root.name = "environment.secondary-motion";
    this.buildGrass();
    this.buildMotes();

    const geometry = new THREE.PlaneGeometry(3.2, 1.55, 8, 4);
    this.water = new THREE.Mesh(geometry, this.waterMaterial);
    this.water.name = "secondary.water.low-resolution";
    this.water.rotation.x = -Math.PI / 2;
    this.water.position.set(4.65, 0.018, 3.45);
    this.root.add(this.water);
    const attribute = geometry.attributes.position;
    for (let i = 0; i < attribute.count; i += 1) {
      const localX = attribute.getX(i);
      const localY = attribute.getY(i);
      this.waterRest.push({
        localX,
        localY,
        world: { x: this.water.position.x + localX, y: 0, z: this.water.position.z - localY },
      });
    }
    const edge = new THREE.LineSegments(new THREE.EdgesGeometry(geometry), this.waterEdgeMaterial);
    this.water.add(edge);
  }

  update(time: number, impulses: readonly PresentationImpulse[], windStrength: number, palette: Palette): void {
    this.grassMaterial.color.setHex(palette.neutral).lerp(new THREE.Color(0x4f8356), 0.56);
    this.moteMaterial.color.setHex(palette.highlight);
    this.waterMaterial.color.setHex(palette.floorAccent).lerp(new THREE.Color(palette.player), 0.34);
    this.waterEdgeMaterial.color.setHex(palette.playerAccent);

    for (const blade of this.grass) {
      const sample = sampleSecondaryMotion({ position: blade.position, time, impulses, response: GRASS_RESPONSE, windStrength });
      blade.pivot.rotation.x = -sample.displacement.z * 2.1;
      blade.pivot.rotation.z = sample.displacement.x * 2.1;
    }

    for (const mote of this.motes) {
      const sample = sampleSecondaryMotion({ position: mote.position, time, impulses, response: MOTE_RESPONSE, windStrength });
      mote.pivot.position.set(
        mote.position.x + sample.displacement.x * 1.6,
        mote.position.y + Math.sin(time * 1.35 + mote.phase) * 0.11 + sample.displacement.y,
        mote.position.z + sample.displacement.z * 1.6,
      );
      mote.pivot.rotation.y = time * 0.8 + mote.phase;
    }

    const vertices = this.water.geometry.attributes.position;
    for (let i = 0; i < vertices.count; i += 1) {
      const rest = this.waterRest[i]!;
      const sample = sampleSecondaryMotion({ position: rest.world, time, impulses, response: WATER_RESPONSE, windStrength });
      vertices.setXYZ(i, rest.localX, rest.localY, sample.displacement.y * 1.65);
    }
    vertices.needsUpdate = true;
  }

  dispose(): void {
    this.root.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) child.geometry.dispose();
    });
    this.grassMaterial.dispose();
    this.moteMaterial.dispose();
    this.waterMaterial.dispose();
    this.waterEdgeMaterial.dispose();
  }

  private buildGrass(): void {
    const bladeGeometry = new THREE.PlaneGeometry(0.075, 0.48);
    bladeGeometry.translate(0, 0.24, 0);
    for (let i = 0; i < 34; i += 1) {
      const side = i % 2 === 0 ? -1 : 1;
      const row = Math.floor(i / 2);
      const x = side * (5.58 + (row % 4) * 0.28);
      const z = -3.85 + row * 0.47;
      const pivot = new THREE.Group();
      pivot.name = `secondary.grass.${i}`;
      pivot.position.set(x, 0.01, z);
      pivot.rotation.y = (i * 1.91) % Math.PI;
      const blade = new THREE.Mesh(bladeGeometry.clone(), this.grassMaterial);
      blade.scale.y = 0.7 + (i % 5) * 0.08;
      pivot.add(blade);
      this.root.add(pivot);
      this.grass.push({ pivot, position: { x, y: 0.24, z }, phase: i * 0.73 });
    }
    bladeGeometry.dispose();
  }

  private buildMotes(): void {
    for (let i = 0; i < 12; i += 1) {
      const x = -5.4 + (i % 6) * 2.15;
      const z = -3.4 + Math.floor(i / 6) * 6.6 + (i % 3) * 0.12;
      const y = 0.42 + (i % 4) * 0.23;
      const pivot = new THREE.Group();
      pivot.name = `secondary.mote.${i}`;
      const mote = new THREE.Mesh(new THREE.OctahedronGeometry(0.035 + (i % 3) * 0.01, 0), this.moteMaterial);
      pivot.add(mote);
      this.root.add(pivot);
      this.motes.push({ pivot, position: { x, y, z }, phase: i * 1.37 });
    }
  }
}
